<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useInboxConnectionHealth } from 'dashboard/composables/useInboxConnectionHealth';
import { useAccount } from 'dashboard/composables/useAccount';

/**
 * InboxConnectionHealthNotice
 *
 * Invisible component mounted once in App.vue. Watches the inbox store
 * and fires a single deduplicated toast when one or more inboxes are
 * disconnected.
 *
 * Real WAHA refresh: administrators refresh on mount, account switch and
 * while the tab is visible. A per-account localStorage throttle limits all
 * tabs to one batch every five minutes. The inbox list is fetched first and
 * its returned records are used for the matching account, preventing stale
 * inbox IDs during account switches.
 *
 * Dedup strategy:
 * - A session-scoped Set tracks inbox IDs we've already notified about.
 * - IDs are REMOVED when the inbox reconnects, so a subsequent
 *   disconnection fires a new toast (not silently swallowed).
 * - The set is cleared on account switch and component unmount.
 *
 * CTA:
 * - Single disconnected inbox → settings_inbox_show with accountId +
 *   inboxId + tab=configuration via accountScopedRoute.
 * - Multiple disconnected inboxes → settings_inbox_list with accountId.
 * - Non-admins get no CTA (route requires administrator permission).
 */

const REFRESH_INTERVAL_MS = 300000;
const REFRESH_STORAGE_KEY = 'waha-health-last-refresh';

const { t } = useI18n();
const { accountId, accountScopedRoute } = useAccount();
const {
  disconnectedInboxes,
  hasDisconnectedInboxes,
  disconnectedCount,
  canManageInboxes,
  refreshInboxes,
  refreshWahaStatuses,
} = useInboxConnectionHealth();

// Session-scoped dedup: IDs we've already shown a toast for.
const notifiedIds = ref(new Set());

const buildToastAction = () => {
  if (!canManageInboxes.value) return null;

  // Single disconnected inbox → link directly to its settings page
  if (disconnectedCount.value === 1) {
    const inbox = disconnectedInboxes.value[0];
    return {
      type: 'link',
      message: t('INBOX_CONNECTION_HEALTH.TOAST_ACTION'),
      to: accountScopedRoute('settings_inbox_show', {
        inboxId: inbox.id,
        tab: 'configuration',
      }),
    };
  }

  // Multiple → link to the inbox list
  return {
    type: 'link',
    message: t('INBOX_CONNECTION_HEALTH.TOAST_ACTION'),
    to: accountScopedRoute('settings_inbox_list'),
  };
};

const maybeFireToast = () => {
  const currentIds = new Set(disconnectedInboxes.value.map(i => i.id));

  // Remove IDs that reconnected — allows re-notification on next disconnect
  notifiedIds.value = new Set(
    [...notifiedIds.value].filter(id => currentIds.has(id))
  );

  if (!hasDisconnectedInboxes.value) return;

  // Find newly-disconnected inboxes we haven't notified about
  const newIds = [...currentIds].filter(id => !notifiedIds.value.has(id));
  if (newIds.length === 0) return;

  // Mark all current disconnected inboxes as notified
  notifiedIds.value = new Set([...currentIds]);

  const message =
    disconnectedCount.value === 1
      ? t('INBOX_CONNECTION_HEALTH.TOAST_SINGLE', {
          name: disconnectedInboxes.value[0]?.name || '',
        })
      : t('INBOX_CONNECTION_HEALTH.TOAST_MULTIPLE', {
          count: disconnectedCount.value,
        });

  useAlert(message, buildToastAction());
};

// Watch for changes in disconnected inboxes — fires toast for new ones
// and cleans up IDs that reconnected.
watch(
  () => disconnectedInboxes.value.map(i => i.id).join(','),
  () => maybeFireToast(),
  { immediate: true }
);

// --- Real WAHA status refresh ---
let intervalTimer = null;

const refreshForAccount = async id => {
  if (!canManageInboxes.value || !id) return;

  const storageKey = `${REFRESH_STORAGE_KEY}:${id}`;
  const lastRefreshAt = Number(window.localStorage.getItem(storageKey) || 0);
  if (lastRefreshAt && Date.now() - lastRefreshAt < REFRESH_INTERVAL_MS) return;

  window.localStorage.setItem(storageKey, Date.now().toString());
  const inboxRecords = await refreshInboxes(id);
  if (!inboxRecords || id !== accountId.value) return;

  await refreshWahaStatuses(id, inboxRecords);
};

const stopInterval = () => {
  if (intervalTimer) {
    clearInterval(intervalTimer);
    intervalTimer = null;
  }
};

const startInterval = () => {
  stopInterval();
  intervalTimer = setInterval(() => {
    if (document.visibilityState === 'visible') {
      refreshForAccount(accountId.value);
    }
  }, REFRESH_INTERVAL_MS);
};

// Refresh WAHA status when tab becomes visible — picks up real session
// status from the WAHA API. No request when hidden.
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    refreshForAccount(accountId.value);
  }
};

// Clear dedup on account switch, refresh inbox list for the new account,
// and trigger WAHA status refresh for the new account's WAHA inboxes.
watch(accountId, id => {
  notifiedIds.value = new Set();
  refreshForAccount(id);
});

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  // Initial WAHA status check on mount
  refreshForAccount(accountId.value);
  startInterval();
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  stopInterval();
  notifiedIds.value = new Set();
});
</script>

<template>
  <div role="status" aria-live="polite" class="sr-only">
    <template v-if="hasDisconnectedInboxes">
      {{
        disconnectedCount === 1
          ? t('INBOX_CONNECTION_HEALTH.TOAST_SINGLE', {
              name: disconnectedInboxes[0]?.name || '',
            })
          : t('INBOX_CONNECTION_HEALTH.TOAST_MULTIPLE', {
              count: disconnectedCount,
            })
      }}
    </template>
  </div>
</template>
